import { useState, useCallback, useEffect, useContext } from "react";
import { CheckBox } from "devextreme-react/check-box";
import { NumberBox } from "devextreme-react/number-box";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent } from "@mui/material";
import { OidcIdentityContext } from "@rsmus/react-auth";

import {
    NotificationPreferencesProps,
    NotificationSettings,
    DEFAULT_NOTIFICATION_SETTINGS,
} from "./NotificationPreferences.types";
import "./NotificationPreferences.scss";
import ActionIconButton from "../../commonComponents/ActionIconButton";
import CustomTable, { TableColumn } from "../../commonComponents/CustomTable";
import { EmailDetailsDto, NotificationActionRequest, RolloverEmailNotification } from "../../../api-client-nswag/rollover-client";
import { rollOverClientApi, userAssignmentClientApi } from "../../../api-client-nswag/taxportal-client-runtime";
import { UserAssignmentListDto } from "../../../api-client-nswag/userassignment-client";
import { SnackbarMessage, useSnackbar } from "../../../hooks";
import { UserGroups } from "../../../constants";
import { ROLLOVER_STATUSES } from "../../../pages/Rollovers/Rollovers.constants";

const VALIDATION_MESSAGE =
    "The number of days selected exceeds the time remaining before the scheduled date. Please choose a valid number of days or update the scheduled rollover date.";

interface RecordItem {
    emailType: string;
    scheduledDate: string;
    recipients: string[];
    status: string;
    action?: ActionOption[] | null;
    notes: string;
}

interface ActionOption {
    label: string;
    value: string;
    onClick: () => void;
}


const getNotificationType = (emailType: string) => {
    switch (emailType) {
        case "First Email":
            return RolloverEmailNotification.ROLLOVER_INITIATED;
        case "Second Email":
            return RolloverEmailNotification.REMINDER;
        case "Cancellation Email":
            return RolloverEmailNotification.CANCELED;
        default:
            return RolloverEmailNotification.ROLLOVER_INITIATED;
    }
};


const columns: TableColumn<RecordItem>[] = [
    { header: "Email Type", field: "emailType" },
    { header: "Scheduled / Sent Date", field: "scheduledDate", maxWidth: "110px" },
    {
        header: "Recipients",
        maxWidth: "220px",
        render: (row) => (
            <div style={{
                display: "flex", flexWrap: "wrap", gap: "4px", maxHeight: "60px",
                overflowY: "auto",
            }}>
                {row.recipients.map((email: any, index: any) => (
                    <a
                        key={index}
                        href={`mailto:${email}`}
                        style={{
                            color: "#1a73e8",
                            textDecoration: "underline",
                            cursor: "pointer",
                        }}
                    >
                        {email}
                        {index < row.recipients.length - 1 && ","}
                    </a>
                ))}
            </div>
        ),
    },
    { header: "Status", field: "status", maxWidth: "135px" },
    {
        header: "Action",
        align: "center",
        render: (row: any) => {
            if (!row.action || row.action.length === 0) {
                return null;
            }

            return (
                <ActionIconButton
                    icon={<MoreVertIcon />}
                    title="See Details"
                    options={row.action}
                    className="dx-datagrid"
                />
            );
        },
    },
    { header: "Notes", field: "notes", maxWidth: "240px" },
];

export default function NotificationPreferences({
    settings: initialSettings,
    scheduledDate,
    onSettingsChange,
    disabled = false,
    onValidationChange,
    isReadOnly,
    selectedRolloverItem
}: NotificationPreferencesProps) {

    const settings = initialSettings ?? DEFAULT_NOTIFICATION_SETTINGS;
    const { showSnackbar } = useSnackbar();
    const { user } = useContext(OidcIdentityContext);
    const [alertState, setAlertState] = useState<{
        showAlertModal: boolean;
        selectedEmailNotificationType: RolloverEmailNotification | null;
        alertAction: string | null;
    }>({
        showAlertModal: false,
        selectedEmailNotificationType: null,
        alertAction: null,
    });
    const [validationError, setValidationError] = useState<string | null>(null);
    const [isEmptyDaysError, setIsEmptyDaysError] = useState<boolean>(false);
    const [records, setRecords] = useState<RecordItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [isTransparentLoader, setIsTransparentLoader] = useState(false);

    useEffect(() => {
        if (
            settings.notifyClient &&
            settings.secondEmail &&
            (settings.secondEmailDays === null || settings.secondEmailDays === undefined)
        ) {
            setValidationError(VALIDATION_MESSAGE);
            setIsEmptyDaysError(true);
            return;
        }

        if (
            settings.notifyClient &&
            settings.secondEmail &&
            scheduledDate &&
            typeof settings.secondEmailDays === "number"
        ) {
            const MS_PER_DAY = 1000 * 60 * 60 * 24;
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const scheduled = new Date(scheduledDate);
            scheduled.setHours(0, 0, 0, 0);

            const daysUntilScheduled = Math.ceil((scheduled.getTime() - today.getTime()) / MS_PER_DAY);

            if ((settings.secondEmailDays ?? 0) > daysUntilScheduled) {
                setValidationError(VALIDATION_MESSAGE);
                setIsEmptyDaysError(false);
            } else {
                setValidationError(null);
                setIsEmptyDaysError(false);
            }
        } else {
            setValidationError(null);
            setIsEmptyDaysError(false);
        }
    }, [
        settings.notifyClient,
        settings.secondEmail,
        settings.secondEmailDays,
        scheduledDate,
    ]);

    useEffect(() => {
        onValidationChange?.(validationError === null);
    }, [validationError, onValidationChange]);

    useEffect(() => {
        if (!selectedRolloverItem?.id) return;

        const controller = new AbortController();

        fetchEmailDetails(controller.signal);

        return () => controller.abort();
    }, [selectedRolloverItem?.id]);


    const fetchEmailDetails = async (signal?: AbortSignal) => {
        if (!selectedRolloverItem?.id) return;

        try {
            setLoading(true);
            const [emailResponse, userAssignmentsResponse] = await Promise.all([
                rollOverClientApi.getEmailDetails(
                    selectedRolloverItem.id,
                    signal
                ),
                userAssignmentClientApi.userAssignment_GetUserAssignmentList(
                    selectedRolloverItem?.hqSiteId,
                    null,
                    null,
                    null
                )
            ]);

            const userAssignments: UserAssignmentListDto[] = userAssignmentsResponse?.data?.data ?? [];
            const mapped = mapApiToRecords(emailResponse, userAssignments);
            setRecords(mapped);
        } catch (err: any) {
            showSnackbar({ message: SnackbarMessage.ERROR, severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleNotificationAction = async (action: string) => {
        try {
            setIsTransparentLoader(true);
            setAlertState(prev => ({ ...prev, showAlertModal: false }));
            const notificationActionPayload: NotificationActionRequest = {
                hqSiteId: selectedRolloverItem?.hqSiteId ?? 0,
                rolloverTrackId: selectedRolloverItem?.id ?? 0,
                notificationType: alertState.selectedEmailNotificationType!,
                userName: user?.profile.preferred_username!,
                isResend: action === "Resend" ? true : false,
                isCancel: action === "Cancel" ? true : false,
            }

            const response = await rollOverClientApi.notificationAction(notificationActionPayload);
            setIsTransparentLoader(false);
            await fetchEmailDetails();

        } catch (error) {
            const { message } = error as any;
            setIsTransparentLoader(false);
            setAlertState(prev => ({ ...prev, showAlertModal: false }));
            showSnackbar({ message: message, severity: "error" });
        }

    }

    const handleResendClick = (notificationType: RolloverEmailNotification) => {
        setAlertState(prev => ({ selectedEmailNotificationType: notificationType, alertAction: "Resend", showAlertModal: true }));
    };

    const handleCancelClick = (notificationType: RolloverEmailNotification) => {
        setAlertState(prev => ({ selectedEmailNotificationType: notificationType, alertAction: "Cancel", showAlertModal: true }));
    };

    const mapApiToRecords = (data: EmailDetailsDto[], userAssignments: UserAssignmentListDto[]): RecordItem[] => {
        return data.map((item) => {
            const actions: any[] = [];

            const notificationType = getNotificationType(item.emailType);
            const commonGroups = [
                UserGroups.CLIENT_USER,
                UserGroups.CS_AUDIT,
                UserGroups.CS_NON_AUDIT,
                UserGroups.POWER_USER,
                UserGroups.CS_POWER_USER,
                UserGroups.FUNCTIONAL_LEAD,
                UserGroups.CS_FUNCTIONAL_LEAD,
                UserGroups.PROJECT_LEAD,
                UserGroups.CS_PROJECT_LEAD,
                UserGroups.RSM_ENGAGEMENT_TEAM,
                UserGroups.CS_ENGAGEMENT_TEAM,
                UserGroups.CS_ENGAGEMENT_TEAM_LIMITED,
                UserGroups.RSM_MEMBER_FIRM_GROUP,
                UserGroups.CS_CUSTOM_USER_GROUP,
            ];

            const allowedGroups =
                notificationType === RolloverEmailNotification.CANCELED
                    ? [UserGroups.CONTENT_ADMIN, ...commonGroups]
                    : commonGroups;

            const expectedUsers = (userAssignments || []).filter((x) =>
                x.userGroups?.split(',').some((g: string) =>
                    allowedGroups.some(
                        (cg) => g.trim().toLowerCase() === cg.toLowerCase()
                    )
                )
            );

            const expectedEmails = expectedUsers
                .map((u) => u.userEmail?.toLowerCase())
                .filter((email): email is string => !!email);

            const actualEmails = (item.recipients || [])
                .map((e: string) => e.toLowerCase());

            const recipients = Array.from(
                new Set([
                    ...actualEmails.map(email => email.trim().toLowerCase()),
                    ...expectedEmails.map(email => email.trim().toLowerCase()),
                ])
            );

            const isPartial = actualEmails.length > 0 && expectedEmails.some(email => !actualEmails.includes(email));
            const isRolloverExecuted = !!selectedRolloverItem?.rollOverStatus && Object.values(ROLLOVER_STATUSES).includes(selectedRolloverItem.rollOverStatus);

            let status = item.status;
            if (status === "Successful" && isPartial && !isRolloverExecuted) {
                status = "Partially Successful";
            }

            if (item.canCancel) {
                actions.push({
                    label: "Cancel",
                    value: "cancel",
                    onClick: () =>
                        handleCancelClick(notificationType),
                });
            }

            if (item.canResend) {
                actions.push({
                    label: "Resend",
                    value: "resend",
                    onClick: () =>
                        handleResendClick(notificationType),
                });
            }

            return {
                emailType: item.emailType,
                scheduledDate: item.scheduledDate ?? "Not Selected",
                recipients: isRolloverExecuted ? actualEmails : recipients,
                status,
                action: actions,
                notes: item.notes,
            };
        });
    };

    // ✅ Handlers
    const updateSettings = useCallback(
        (updater: NotificationSettings | ((prev: NotificationSettings) => NotificationSettings)) => {
            onSettingsChange?.(updater);
        },
        [onSettingsChange]
    );

    const handleNotifyClientChange = (e: any) => {
        const enabled = e.value;

        updateSettings(prev => ({
            ...prev,
            notifyClient: enabled,
            firstEmail: enabled ? prev.firstEmail : false,
            secondEmail: enabled ? prev.secondEmail : false,
            cancelationEmail: enabled ? prev.cancelationEmail : false,
        }));
    };

    const handleFirstEmailChange = (e: any) => {
        const newFirstEmail = e.value;

        updateSettings(prev => ({
            ...prev,
            firstEmail: newFirstEmail,
            notifyClient:
                newFirstEmail || prev.secondEmail || prev.cancelationEmail,
        }));
    };

    const handleSecondEmailChange = (e: any) => {
        const newSecondEmail = e.value;

        updateSettings(prev => ({
            ...prev,
            secondEmail: newSecondEmail,
            secondEmailDays: newSecondEmail
                ? (typeof prev.secondEmailDays === "number"
                    ? prev.secondEmailDays
                    : DEFAULT_NOTIFICATION_SETTINGS.secondEmailDays)
                : undefined,
            notifyClient:
                newSecondEmail || prev.firstEmail || prev.cancelationEmail,
        }));
    };

    const handleSecondEmailDaysChange = (e: any) => {
        let value = e.value;

        if (value === null || value === undefined || isNaN(value)) {
            value = undefined;
        }

        updateSettings(prev => ({
            ...prev,
            secondEmailDays: value,
        }));
    };

    const handleKeyDown = (e: any) => {
        if (["-", "e", "E"].includes(e.event.key)) {
            e.event.preventDefault();
        }
    };

    const handleCancelationEmailChange = (e: any) => {
        const newCancelationEmail = e.value;

        updateSettings(prev => ({
            ...prev,
            cancelationEmail: newCancelationEmail,
            notifyClient:
                newCancelationEmail || prev.firstEmail || prev.secondEmail,
        }));
    };

    return (
        <div className="notification-preferences">
            {isTransparentLoader && (
                <div className="overlay-loader">
                    <CircularProgress />
                </div>
            )}
            <div className="notification-preferences__content">
                <div className="notification-preferences__option notification-preferences__option--parent">
                    <CheckBox
                        value={settings.notifyClient}
                        onValueChanged={handleNotifyClientChange}
                        disabled={disabled || !scheduledDate}
                    />
                    <span className="notification-preferences__label">Notify Client</span>
                    <Tooltip
                        title="Send rollover email notifications to client users as a reminder to download files they need prior to files being deleted from the site as part of rollover activities."
                        arrow
                        placement="top"
                    >
                        <InfoOutlinedIcon
                            className="notification-preferences__info-icon"
                            fontSize="small"
                        />
                    </Tooltip>
                </div>

                <div className="notification-preferences__option notification-preferences__option--child">
                    <CheckBox
                        value={settings.firstEmail}
                        onValueChanged={handleFirstEmailChange}
                        disabled={disabled || !scheduledDate}
                    />
                    <span className="notification-preferences__label">
                        First Email: Immediately after rollover
                    </span>
                </div>

                <div
                    className={`notification-preferences__option notification-preferences__option--child `}
                >
                    <CheckBox
                        value={settings.secondEmail}
                        onValueChanged={handleSecondEmailChange}
                        disabled={disabled || !scheduledDate}
                    />
                    <span className="notification-preferences__label">
                        Second Email: Sends a reminder
                    </span>

                    <NumberBox
                        className={`notification-preferences__days-input ${validationError && !isReadOnly
                            ? "notification-preferences__days-input--error"
                            : ""
                            }`}
                        value={settings.secondEmailDays ?? undefined}
                        onValueChanged={handleSecondEmailDaysChange}
                        onKeyDown={handleKeyDown}
                        valueChangeEvent="input"
                        width={36}
                        height={40}
                        showSpinButtons={false}
                        disabled={disabled || !settings.secondEmail}
                        min={0}
                    />

                    <div className="notification-preferences__inline-container">
                        <span className="notification-preferences__sub-label-inline">
                            days before the scheduled rollover date.
                        </span>

                        {validationError && !isEmptyDaysError && !isReadOnly && (
                            <Tooltip title={validationError} arrow placement="bottom">
                                <WarningAmberIcon className="notification-preferences__warning-icon" />
                            </Tooltip>
                        )}
                    </div>
                </div>

                <div className="notification-preferences__option notification-preferences__option--child">
                    <CheckBox
                        value={settings.cancelationEmail}
                        onValueChanged={handleCancelationEmailChange}
                        disabled={disabled || !scheduledDate}
                    />
                    <span className="notification-preferences__label">
                        Cancellation Email: Sends a notice if the rollover is canceled.
                    </span>
                </div>
            </div>
            {isReadOnly &&
                <div>
                    <div style={{ width: "100%", height: "1px", backgroundColor: "#e0e0e0", margin: "16px 0" }} />
                    <p style={{ color: '#515356', display: "flex", alignItems: "center", gap: "8px", fontSize: "18px", marginLeft: "14px", fontWeight: '700' }}>Email Details</p>
                    {loading ? (
                        <div style={{ height: `100px`, display: 'flex', justifyContent: "center", alignItems: "center" }}>
                            <Box display="flex" justifyContent="center" alignItems="center" p={1}>
                                <CircularProgress />
                            </Box>
                        </div>) :
                        <div className="email-details">
                            <CustomTable<RecordItem> columns={columns} data={records ?? []} />
                        </div>
                    }
                </div>
            }
            <Dialog
                open={alertState.showAlertModal}
                onClose={() => setAlertState(prev => ({ ...prev, showAlertModal: false }))}
            >
                <DialogContent style={{ fontSize: "16px" }}>{`Are you sure you want to ${alertState.alertAction}?`}</DialogContent>
                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={() => setAlertState(prev => ({ ...prev, showAlertModal: false }))}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleNotificationAction(alertState.alertAction!)}
                    >
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
